import { Card, Skeleton, Typography } from 'antd';
import classNames from 'classnames';
import BasePagination from 'components/atoms/base-pagination';
import { memo, type ReactNode, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Routes } from 'router/routes';
import BaseEmpty from 'components/atoms/oneplatform/base-empty';

import styles from './styles.module.scss';
import HistoryEntry from './history-entry';

import type { IHistoryActivitiesProps, IHistoryActivityItem } from './types';

const { Text } = Typography;

const sanitizeEntries = (
  entries?: Array<IHistoryActivityItem | null | undefined>,
): IHistoryActivityItem[] =>
  entries?.filter((entry): entry is IHistoryActivityItem => Boolean(entry)) ??
  [];

const TodayHistoryActivities = memo(
  ({
    title,
    entries,
    className,
    emptyMessage,
    page = 1,
    total = 1,
    pageSize = 1,
    isLoading = false,
    isFetching = false,
    onPageChange,
  }: IHistoryActivitiesProps) => {
    const visibleEntries = useMemo(() => sanitizeEntries(entries), [entries]);
    const cardAriaLabel = title || 'Today history activities';
    const navigate = useNavigate();
    const showSkeleton = isLoading || isFetching;
    const skeletonItems = useMemo(
      () =>
        Array.from({ length: Math.max(pageSize ?? 1, 1) }, (_, index) => index),
      [pageSize],
    );

    const handleEntryNavigate = useCallback(
      (patientGuid: string) => {
        navigate(Routes.patientGuid(patientGuid));
      },
      [navigate],
    );

    const handlePageChange = (newPage: number) => {
      if (onPageChange) {
        onPageChange(newPage);
      }
    };

    let content: ReactNode;

    if (showSkeleton) {
      content = (
        <div
          className={styles.entries}
          data-testid="today-history-activities-skeleton"
        >
          {skeletonItems.map((index) => (
            <div
              key={`history-skeleton-${index}`}
              className={styles.entry}
              data-testid="today-history-activities-skeleton-item"
            >
              <Skeleton
                active
                paragraph={{ rows: 1, width: '60%' }}
                title={false}
              />
            </div>
          ))}
        </div>
      );
    } else if (visibleEntries.length > 0) {
      content = (
        <div className={styles.entries}>
          {visibleEntries.map((entry) => (
            <HistoryEntry
              key={entry.id}
              entry={entry}
              onEntryNavigate={handleEntryNavigate}
            />
          ))}
        </div>
      );
    } else {
      content = (
        <BaseEmpty
          description={emptyMessage || 'No history activities available'}
        />
      );
    }

    return (
      <Card
        className={classNames(styles['history-card'], className)}
        variant="borderless"
        role="region"
        aria-label={cardAriaLabel}
        aria-busy={showSkeleton}
      >
        {title || total > 1 ? (
          <div className={styles.header}>
            {title ? <Text className={styles.title}>{title}</Text> : null}
            {visibleEntries.length > 0 && (
              <BasePagination
                simple
                page={page}
                total={total}
                pageSize={pageSize}
                onChange={handlePageChange}
                className={styles.pagination}
                showSizeChanger={false}
              />
            )}
          </div>
        ) : null}

        {content}
      </Card>
    );
  },
);

TodayHistoryActivities.displayName = 'TodayHistoryActivities';

export default TodayHistoryActivities;
